import React from 'react';
import { FiList, FiPlus } from 'react-icons/fi';
import { useDrop } from 'react-dnd';
import { cn } from '../../lib/utils';
import { ItemTypes, DragItem } from '../../lib/dnd-types';

interface UnassignedTasksDropZoneProps {
  taskCount: number;
  onMoveToProject?: (todoId: string, projectId: string | null) => void;
  className?: string;
}

export const UnassignedTasksDropZone = React.memo<UnassignedTasksDropZoneProps>(({
  taskCount,
  onMoveToProject,
  className
}) => {
  // Drop functionality
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.TODO_CARD,
    drop: (item: DragItem) => {
      if (onMoveToProject && item.todo.project_id !== null) {
        onMoveToProject(item.id, null);
      }
    },
    canDrop: (item: DragItem) => {
      // Can only drop if currently assigned to a project
      return item.todo.project_id !== null;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [onMoveToProject]);

  return (
    <div 
      ref={drop}
      className={cn(
        "flex items-center gap-3 px-3 py-2 bg-muted/30 rounded-lg transition-all duration-200",
        isOver && canDrop && "border-2 border-dashed border-primary bg-primary/10 scale-105",
        isOver && !canDrop && "border-2 border-dashed border-muted-foreground/30 bg-muted/20",
        canDrop && "hover:bg-muted/50",
        className
      )}
    >
      <div className={cn(
        "w-6 h-6 rounded-full flex items-center justify-center transition-colors",
        isOver && canDrop ? "bg-primary text-primary-foreground" : "bg-muted-foreground/20"
      )}>
        {isOver && canDrop ? (
          <FiPlus className="w-3 h-3" />
        ) : (
          <FiList className="w-3 h-3 text-muted-foreground" />
        )}
      </div>
      <span className={cn(
        "text-sm font-medium transition-colors",
        isOver && canDrop ? "text-primary" : "text-muted-foreground"
      )}>
        Unassigned Tasks ({taskCount})
      </span>
      {isOver && canDrop && (
        <span className="text-xs text-primary bg-primary/20 px-2 py-0.5 rounded-full animate-pulse">
          Drop to unassign
        </span>
      )}
    </div>
  );
});

UnassignedTasksDropZone.displayName = 'UnassignedTasksDropZone';